import type {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  ReactNode,
  TransitionEvent as ReactTransitionEvent,
} from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DocumentSection, Proposal } from '@shared/types';
import { PageDomIdPrefixContext, PageFrame } from '../pages/Page';
import { PageArchetype } from '../pages/registry';
import { READ_ONLY_ACTIONS, type PageActions } from '../pages/types';
import { tacticGroupDisplayTitle, visibleSections } from '../lib/plan';
import { themeStyle } from '../lib/theme';
import { useContentLibrary } from '../state/ContentLibraryContext';
import {
  mergeSequence,
  pendingArrivalAdvances,
  type PendingPagesState,
  type SequenceItem,
} from '../state/pendingPages';
import { PendingGallerySlide } from './PendingGallerySlide';
import './ProposalGallery.css';

type TransitionState = 'opening' | 'browsing' | 'closing';
type SharedPhase = 'from' | 'to';

export type GalleryRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

const EASE = 'cubic-bezier(.22,1,.36,1)';
const OPEN_DURATION = 400;
const CLOSE_DURATION = 350;
const HOVER_SETTLE_DURATION = 180;

export const isRectInside = (rect: GalleryRect | null, bounds: GalleryRect | null): boolean =>
  Boolean(
    rect &&
      bounds &&
      rect.left >= bounds.left &&
      rect.top >= bounds.top &&
      rect.left + rect.width <= bounds.left + bounds.width &&
      rect.top + rect.height <= bounds.top + bounds.height,
  );

export const gallerySlideGap = (viewportWidth: number): number => {
  if (viewportWidth < 900) return 0;
  return viewportWidth < 1280 ? 96 : 112;
};

export const galleryControlsTop = (stage: GalleryRect): number => Math.max(8, stage.top - 64);

export const galleryWindowIndices = (length: number, centers: number[]): number[] => {
  const indices = new Set<number>();
  centers.forEach((center) => {
    for (let index = center - 2; index <= center + 2; index += 1) {
      if (index >= 0 && index < length) indices.add(index);
    }
  });
  return [...indices].sort((left, right) => left - right);
};

export const galleryPointerTilt = (
  rect: GalleryRect,
  point: { x: number; y: number },
): { rotateX: number; rotateY: number } => {
  const nx = Math.min(1, Math.max(0, (point.x - rect.left) / rect.width)) - 0.5;
  const ny = Math.min(1, Math.max(0, (point.y - rect.top) / rect.height)) - 0.5;
  return { rotateX: ny * -2.5, rotateY: nx * 2.5 };
};

const isGalleryBackgroundTarget = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  !target.closest(
    '.proposal-gallery__slide, .proposal-gallery__shared, .proposal-gallery__message-bar, button, a, input, textarea, select, [contenteditable], [role="button"]',
  );

export const isGalleryEditorTag = (tagName: string, contentEditable = false): boolean =>
  contentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName.toUpperCase());

/** Keyboard and swipe navigation must yield to the page's inline editors. */
export const isGalleryEditingTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (isGalleryEditorTag(target.tagName, target.isContentEditable) ||
    Boolean(target.closest('[contenteditable], [role="textbox"]')));

export const galleryStage = (bounds: GalleryRect, compact: boolean): GalleryRect => {
  const reservedBelow = compact ? 72 : 64;
  const width = compact
    ? Math.min(bounds.width, Math.max(1, (bounds.height - reservedBelow) * (16 / 9)))
    : Math.min(
        960,
        bounds.width * 0.7,
        Math.max(1, bounds.width - 224),
        Math.max(1, bounds.height - reservedBelow) * (16 / 9),
      );
  const height = width * (9 / 16);
  const visualHeight = compact ? bounds.height - reservedBelow : bounds.height;

  return {
    width,
    height,
    left: bounds.left + (bounds.width - width) / 2,
    top: bounds.top + (visualHeight - height) / 2,
  };
};

const rectFor = (element: Element | null): GalleryRect | null => {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0
    ? { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
    : null;
};

const sourcePreview = (id: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `#${CSS.escape(`proposal-slide-${id}`)} .proposal-slide__preview`,
  );

const titleFor = (
  proposal: Proposal,
  section: DocumentSection,
  productName: (id: string) => string | null,
): string =>
  tacticGroupDisplayTitle(
    proposal,
    section.productIds ?? [],
    section.title,
    productName,
  );

/** A gallery slot is a real, saved section, or a pending-page placeholder splice in (docs/custom-page.md). */
type GallerySlot = SequenceItem<DocumentSection>;

const slotId = (slot: GallerySlot): string => slot.kind === 'item' ? slot.value.id : slot.key;

const slotTitle = (
  slot: GallerySlot,
  proposal: Proposal,
  productName: (id: string) => string | null,
): string => slot.kind === 'item' ? titleFor(proposal, slot.value, productName) : slot.entry.pending.title ?? 'New page';

function SlideRender({
  proposal,
  section,
  prefix,
  actions = READ_ONLY_ACTIONS,
  readOnly = true,
}: {
  proposal: Proposal;
  section: DocumentSection;
  prefix: string;
  actions?: PageActions;
  readOnly?: boolean;
}): ReactElement {
  return (
    <PageDomIdPrefixContext.Provider value={prefix}>
      <PageFrame>
        <PageArchetype
          proposal={proposal}
          section={section}
          actions={actions}
          readOnly={readOnly}
        />
      </PageFrame>
    </PageDomIdPrefixContext.Provider>
  );
}

/** Stable default: a fresh `[]` per render would re-run every memo/effect keyed on it. */
const NO_PENDING_PAGES: PendingPagesState = [];

/** Editable viewer with separate shared-element and carousel systems. */
export function ProposalGallery({
  proposal,
  actions,
  initialSectionId,
  pendingPages = NO_PENDING_PAGES,
  messageBar,
  onActiveSectionChange,
  onClose,
  readOnly = false,
}: {
  proposal: Proposal;
  actions: PageActions;
  /** docs/viewer-sharing.md: a view-only teammate presents the deck but edits nothing. */
  readOnly?: boolean;
  initialSectionId: string;
  /** docs/custom-page.md "Pending page": placeholders spliced into present mode's prev/next sequence. */
  pendingPages?: PendingPagesState;
  messageBar?: ReactNode;
  onActiveSectionChange: (id: string) => void;
  onClose: (id: string) => void;
}): ReactElement | null {
  const { productName } = useContentLibrary();
  const allSections = proposal.document?.sections ?? [];
  const initial = allSections.find((section) => section.id === initialSectionId) ?? null;
  const baseSequence = useMemo(
    () => (initial?.hidden ? [initial] : visibleSections(proposal)),
    [initial, proposal],
  );
  const sequence = useMemo(
    () => mergeSequence(baseSequence, pendingPages),
    [baseSequence, pendingPages],
  );
  const [activeId, setActiveId] = useState(initialSectionId);
  const activeIndex = sequence.findIndex((slot) => slotId(slot) === activeId);
  const active = activeIndex < 0 ? null : sequence[activeIndex];
  // Present mode only slides forward to a new placeholder when the rep is
  // currently viewing the page it's inserted after — never for one already
  // pending when the gallery opened, and never while viewing something else
  // (docs/custom-page.md "What the rep sees" / present mode).
  const seenPendingKeysRef = useRef<Set<string>>(new Set(pendingPages.map((entry) => entry.pending.key)));
  const [transition, setTransition] = useState<TransitionState>('opening');
  const [phase, setPhase] = useState<SharedPhase>('from');
  const [motionReady, setMotionReady] = useState(false);
  const [origin, setOrigin] = useState<GalleryRect | null>(() =>
    rectFor(sourcePreview(initialSectionId)),
  );
  const [stage, setStage] = useState<GalleryRect | null>(null);
  const [renderedIndices, setRenderedIndices] = useState(() =>
    galleryWindowIndices(sequence.length, [Math.max(0, activeIndex)]),
  );
  const [navigationLean, setNavigationLean] = useState<-1 | 0 | 1>(0);
  const [settlingClose, setSettlingClose] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef<HTMLElement | null>(null);
  const fallbackTimerRef = useRef<number | null>(null);
  const navigationTimerRef = useRef<number | null>(null);
  const windowTimerRef = useRef<number | null>(null);
  const tiltFrameRef = useRef<number | null>(null);
  const pointerReleaseTimerRef = useRef<number | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const pointerTiltActiveRef = useRef(false);
  const backgroundPressRef = useRef(false);
  const backgroundReleaseRef = useRef(false);
  const backgroundPointerRef = useRef<{
    id: number;
    x: number;
    y: number;
  } | null>(null);
  const activeSlideRef = useRef<HTMLDivElement>(null);
  const activePointerRef = useRef<HTMLDivElement>(null);
  const operationRef = useRef(0);
  const closeInProgressRef = useRef(false);
  const openedRef = useRef(false);
  const finishedRef = useRef(false);
  const touchStart = useRef<number | null>(null);
  const activeIndexRef = useRef(activeIndex);
  const activeRef = useRef(active);
  const navigateRef = useRef<(index: number) => void>(() => undefined);
  const closeRef = useRef<() => void>(() => undefined);
  const onCloseRef = useRef(onClose);
  const onActiveSectionChangeRef = useRef(onActiveSectionChange);
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  const clearFallback = useCallback(() => {
    if (fallbackTimerRef.current !== null) {
      window.clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const clearNavigationTimers = useCallback(() => {
    if (navigationTimerRef.current !== null) {
      window.clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }
    if (windowTimerRef.current !== null) {
      window.clearTimeout(windowTimerRef.current);
      windowTimerRef.current = null;
    }
  }, []);

  const resetPointerTilt = useCallback(() => {
    pointerRef.current = null;
    if (tiltFrameRef.current !== null) {
      cancelAnimationFrame(tiltFrameRef.current);
      tiltFrameRef.current = null;
    }
    activePointerRef.current?.style.removeProperty('--gallery-pointer-rx');
    activePointerRef.current?.style.removeProperty('--gallery-pointer-ry');
  }, []);

  const clearPointerReleaseTimer = useCallback(() => {
    if (pointerReleaseTimerRef.current !== null) {
      window.clearTimeout(pointerReleaseTimerRef.current);
      pointerReleaseTimerRef.current = null;
    }
  }, []);

  const releasePointerTilt = useCallback(() => {
    resetPointerTilt();
    clearPointerReleaseTimer();
    pointerReleaseTimerRef.current = window.setTimeout(() => {
      pointerTiltActiveRef.current = false;
      pointerReleaseTimerRef.current = null;
    }, 1020);
  }, [clearPointerReleaseTimer, resetPointerTilt]);

  const updatePointerTilt = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (reduced || event.pointerType === 'touch') return;
    pointerTiltActiveRef.current = true;
    clearPointerReleaseTimer();
    pointerRef.current = { x: event.clientX, y: event.clientY };
    if (tiltFrameRef.current !== null) return;
    tiltFrameRef.current = requestAnimationFrame(() => {
      tiltFrameRef.current = null;
      const slide = activeSlideRef.current;
      const pointer = activePointerRef.current;
      const point = pointerRef.current;
      if (!slide || !pointer || !point) return;
      const rect = rectFor(slide);
      if (!rect) return;
      const tilt = galleryPointerTilt(rect, point);
      pointer.style.setProperty('--gallery-pointer-rx', `${tilt.rotateX.toFixed(2)}deg`);
      pointer.style.setProperty('--gallery-pointer-ry', `${tilt.rotateY.toFixed(2)}deg`);
    });
  }, [clearPointerReleaseTimer, reduced]);

  const measureStage = useCallback(() => {
    const bounds = rectFor(rootRef.current?.querySelector('.proposal-gallery__stage') ?? null);
    if (!bounds) return;
    const next = galleryStage(bounds, window.innerWidth < 900);
    setStage((current) =>
      current &&
      current.left === next.left &&
      current.top === next.top &&
      current.width === next.width &&
      current.height === next.height
        ? current
        : next,
    );
  }, []);

  const finishOpening = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    clearFallback();
    sourceRef.current?.classList.remove('proposal-gallery-source-hidden');
    sourceRef.current
      ?.closest('.proposal-slide')
      ?.classList.remove('proposal-slide-gallery-transitioning');
    setTransition('browsing');
  }, [clearFallback]);

  const finishClosing = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    clearFallback();
    const slot = activeRef.current;
    if (!slot) return;
    sourceRef.current?.classList.remove('proposal-gallery-source-hidden');
    onCloseRef.current(slotId(slot));
    window.setTimeout(
      () =>
        sourcePreview(slotId(slot))
          ?.querySelector<HTMLElement>('.page-frame-clickable')
          ?.focus(),
      60,
    );
  }, [clearFallback]);

  useLayoutEffect(() => {
    measureStage();
    const observer = new ResizeObserver(measureStage);
    if (rootRef.current) observer.observe(rootRef.current);
    window.addEventListener('resize', measureStage);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureStage);
    };
  }, [measureStage]);

  activeRef.current = active;
  onCloseRef.current = onClose;
  onActiveSectionChangeRef.current = onActiveSectionChange;

  useEffect(() => {
    const app = document.getElementById('root') as (HTMLElement & { inert?: boolean }) | null;
    const overflow = document.body.style.overflow;
    const hadAria = app?.hasAttribute('aria-hidden') ?? false;
    document.body.style.overflow = 'hidden';
    if (app) {
      app.setAttribute('aria-hidden', 'true');
      app.inert = true;
    }
    rootRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (app) {
        if (!hadAria) app.removeAttribute('aria-hidden');
        app.inert = false;
      }
      sourceRef.current?.classList.remove('proposal-gallery-source-hidden');
      sourceRef.current
        ?.closest('.proposal-slide')
        ?.classList.remove('proposal-slide-gallery-transitioning');
      clearFallback();
      clearNavigationTimers();
      clearPointerReleaseTimer();
      resetPointerTilt();
      pointerTiltActiveRef.current = false;
    };
  }, [
    clearFallback,
    clearNavigationTimers,
    clearPointerReleaseTimer,
    resetPointerTilt,
  ]);

  useEffect(() => {
    if (!active) onCloseRef.current(activeId);
  }, [active, activeId]);

  useLayoutEffect(() => {
    if (openedRef.current || !active || !stage) return;
    openedRef.current = true;
    finishedRef.current = false;
    const source = sourcePreview(slotId(active));
    sourceRef.current = source;
    source?.closest('.proposal-slide')?.classList.add('proposal-slide-gallery-transitioning');
    source?.classList.add('proposal-gallery-source-hidden');
    setOrigin(rectFor(source));
    setPhase('from');
    setMotionReady(false);

    const readyFrame = requestAnimationFrame(() => {
      setMotionReady(true);
      requestAnimationFrame(() => {
        setPhase('to');
        clearFallback();
        fallbackTimerRef.current = window.setTimeout(
          finishOpening,
          reduced ? 160 : OPEN_DURATION + 80,
        );
      });
    });
    return () => cancelAnimationFrame(readyFrame);
  }, [active, clearFallback, finishOpening, reduced, stage]);

  const navigate = useCallback(
    (next: number) => {
      if (
        transition !== 'browsing' ||
        next < 0 ||
        next >= sequence.length ||
        next === activeIndex ||
        closeInProgressRef.current
      ) {
        return;
      }
      resetPointerTilt();
      clearPointerReleaseTimer();
      pointerTiltActiveRef.current = false;
      const direction = next > activeIndex ? -1 : 1;
      setNavigationLean(direction);
      clearNavigationTimers();
      setRenderedIndices((current) => [
        ...new Set([...current, ...galleryWindowIndices(sequence.length, [next])]),
      ].sort((left, right) => left - right));
      const nextId = slotId(sequence[next]!);
      setActiveId(nextId);
      onActiveSectionChangeRef.current(nextId);
      navigationTimerRef.current = window.setTimeout(() => {
        setNavigationLean(0);
        navigationTimerRef.current = null;
      }, 110);
      windowTimerRef.current = window.setTimeout(() => {
        setRenderedIndices(galleryWindowIndices(sequence.length, [next]));
        windowTimerRef.current = null;
      }, 430);
    },
    [
      activeIndex,
      clearNavigationTimers,
      clearPointerReleaseTimer,
      resetPointerTilt,
      sequence,
      transition,
    ],
  );
  activeIndexRef.current = activeIndex;
  navigateRef.current = navigate;

  // docs/custom-page.md "What the rep sees" (present mode): a NEW placeholder
  // pulls the viewer forward one slide only when the rep is currently on the
  // page it's inserted after; viewing anything else, it just joins the
  // sequence. `seenPendingKeysRef` excludes anything already pending at
  // mount, so opening the gallery onto a page that already has a queued
  // placeholder doesn't yank the rep off the page they just chose.
  useEffect(() => {
    for (const entry of pendingPages) {
      if (seenPendingKeysRef.current.has(entry.pending.key)) continue;
      seenPendingKeysRef.current.add(entry.pending.key);
      if (transition !== 'browsing' || !pendingArrivalAdvances(activeId, entry.pending)) continue;
      const index = sequence.findIndex((slot) => slot.kind === 'pending' && slot.key === entry.pending.key);
      if (index !== -1) navigate(index);
    }
  }, [activeId, navigate, pendingPages, sequence, transition]);

  const close = useCallback(async () => {
    if (
      !active ||
      transition !== 'browsing' ||
      !stage ||
      closeInProgressRef.current
    ) {
      return;
    }
    closeInProgressRef.current = true;
    const operation = ++operationRef.current;
    const needsSettle =
      !reduced &&
      (pointerTiltActiveRef.current ||
        (activeSlideRef.current?.matches(':hover') ?? false));
    clearPointerReleaseTimer();
    resetPointerTilt();
    clearNavigationTimers();
    setNavigationLean(0);
    if (needsSettle) {
      setSettlingClose(true);
      await new Promise<void>((resolve) => {
        const pointer = activePointerRef.current;
        if (!pointer) {
          resolve();
          return;
        }
        let timer = 0;
        const finish = () => {
          pointer.removeEventListener('transitionend', onTransitionEnd);
          window.clearTimeout(timer);
          resolve();
        };
        const onTransitionEnd = (event: TransitionEvent) => {
          if (event.target === pointer && event.propertyName === 'transform') finish();
        };
        pointer.addEventListener('transitionend', onTransitionEnd);
        timer = window.setTimeout(finish, HOVER_SETTLE_DURATION + 60);
      });
      if (operation !== operationRef.current) return;
    }
    pointerTiltActiveRef.current = false;
    const activeSlotId = slotId(active);
    const card = document.getElementById(`proposal-slide-${activeSlotId}`);
    const grid = document.querySelector<HTMLElement>('.proposal-grid-region');
    let targetSource = sourcePreview(activeSlotId);
    targetSource
      ?.closest('.proposal-slide')
      ?.classList.add('proposal-slide-gallery-transitioning');
    let target = rectFor(targetSource);

    if (!isRectInside(target, rectFor(grid))) {
      card?.scrollIntoView({ block: 'center', behavior: 'auto' });
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
      );
      if (operation !== operationRef.current) return;
      targetSource = sourcePreview(activeSlotId);
      targetSource
        ?.closest('.proposal-slide')
        ?.classList.add('proposal-slide-gallery-transitioning');
      target = rectFor(targetSource);
    }

    const destination = isRectInside(target, rectFor(grid)) ? target : null;
    targetSource?.classList.add('proposal-gallery-source-hidden');
    sourceRef.current = targetSource;
    finishedRef.current = false;
    setOrigin(destination);
    setMotionReady(false);
    setPhase('to');
    setTransition('closing');
    setSettlingClose(false);

    requestAnimationFrame(() => {
      setMotionReady(true);
      requestAnimationFrame(() => {
        if (operation !== operationRef.current) return;
        setPhase('from');
        clearFallback();
        fallbackTimerRef.current = window.setTimeout(
          finishClosing,
          reduced ? 160 : CLOSE_DURATION + 80,
        );
      });
    });
  }, [
    active,
    clearFallback,
    clearNavigationTimers,
    clearPointerReleaseTimer,
    finishClosing,
    reduced,
    resetPointerTilt,
    stage,
    transition,
  ]);
  closeRef.current = () => {
    void close();
  };

  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (isGalleryEditingTarget(event.target)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        navigateRef.current(activeIndexRef.current + 1);
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        navigateRef.current(activeIndexRef.current - 1);
        return;
      }
      if (event.key !== 'Tab' || !rootRef.current) return;
      const items = Array.from(
        rootRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"], [role="textbox"]',
        ),
      ).filter((item) => !item.closest('[aria-hidden="true"]'));
      const index = items.indexOf(document.activeElement as HTMLElement);
      if (event.shiftKey && index <= 0) {
        event.preventDefault();
        items.at(-1)?.focus();
      } else if (!event.shiftKey && index === items.length - 1) {
        event.preventDefault();
        items[0]?.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => document.removeEventListener('keydown', keydown);
  }, []);

  if (!active) return null;

  const viewerStage =
    stage ??
    galleryStage(
      {
        width: Math.max(1, window.innerWidth - 64),
        height: Math.max(1, window.innerHeight - 100),
        left: 32,
        top: 68,
      },
      window.innerWidth < 900,
    );
  const scalar = origin ? origin.width / viewerStage.width : 1;
  const fromTransform = origin
    ? `translate(${origin.left - viewerStage.left}px, ${origin.top - viewerStage.top}px) scale(${scalar})`
    : 'none';
  const slideGap = gallerySlideGap(window.innerWidth);
  const nearby = renderedIndices
    .map((index) => ({ slot: sequence[index], index }))
    .filter((entry): entry is { slot: GallerySlot; index: number } =>
      Boolean(entry.slot),
    );
  const sharedTransform = reduced ? 'none' : phase === 'from' ? fromTransform : 'none';

  const handleSharedTransitionEnd = (event: ReactTransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const expectedProperty = reduced || !origin ? 'opacity' : 'transform';
    if (event.propertyName !== expectedProperty) return;
    if (transition === 'opening' && phase === 'to') finishOpening();
    if (transition === 'closing' && phase === 'from') finishClosing();
  };

  return createPortal(
    <div
      ref={rootRef}
      className={`proposal-gallery proposal-gallery--${transition} proposal-gallery--phase-${phase}${motionReady ? ' proposal-gallery--motion-ready' : ''}${navigationLean ? ' proposal-gallery--leaning' : ''}${settlingClose ? ' proposal-gallery--settling-close' : ''}${reduced ? ' proposal-gallery--reduced' : ''}`}
      style={
        {
          ...themeStyle(proposal.document?.theme),
          '--gallery-stage-width': `${viewerStage.width}px`,
          '--gallery-slide-gap': `${slideGap}px`,
        } as CSSProperties
      }
      role="dialog"
      aria-modal="true"
      aria-label="Proposal gallery"
      tabIndex={-1}
      onPointerDownCapture={(event) => {
        backgroundPressRef.current = isGalleryBackgroundTarget(event.target);
        backgroundReleaseRef.current = false;
        backgroundPointerRef.current = backgroundPressRef.current
          ? { id: event.pointerId, x: event.clientX, y: event.clientY }
          : null;
      }}
      onPointerMoveCapture={(event) => {
        const start = backgroundPointerRef.current;
        if (
          start?.id === event.pointerId &&
          Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8
        ) {
          backgroundPressRef.current = false;
          backgroundReleaseRef.current = false;
          backgroundPointerRef.current = null;
        }
      }}
      onPointerUpCapture={(event) => {
        backgroundReleaseRef.current =
          backgroundPressRef.current && isGalleryBackgroundTarget(event.target);
        backgroundPointerRef.current = null;
      }}
      onPointerCancelCapture={() => {
        backgroundPressRef.current = false;
        backgroundReleaseRef.current = false;
        backgroundPointerRef.current = null;
      }}
      onClick={(event) => {
        const dismiss =
          backgroundPressRef.current &&
          backgroundReleaseRef.current &&
          isGalleryBackgroundTarget(event.target);
        backgroundPressRef.current = false;
        backgroundReleaseRef.current = false;
        backgroundPointerRef.current = null;
        if (dismiss) closeRef.current();
      }}
    >
      <div className="proposal-gallery__backdrop" />
      <header className="proposal-gallery__header">
        <span>
          {slotTitle(active, proposal, productName)} ·{' '}
          {active.kind === 'pending'
            ? 'New page'
            : active.value.hidden
              ? 'Hidden page'
              : `Page ${activeIndex + 1} of ${sequence.length}`}
        </span>
      </header>
      <div
        className="proposal-gallery__header-actions"
        style={{
          top: galleryControlsTop(viewerStage),
          right: window.innerWidth - (viewerStage.left + viewerStage.width),
        }}
      >
        <button
          type="button"
          className="proposal-gallery__close"
          onClick={closeRef.current}
          disabled={transition !== 'browsing' || settlingClose}
          aria-label="Close presentation"
        >
          ×
        </button>
      </div>
      <main
        className="proposal-gallery__stage"
        onTouchStart={(event) => {
          if (isGalleryEditingTarget(event.target)) {
            touchStart.current = null;
            return;
          }
          touchStart.current = event.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(event) => {
          if (isGalleryEditingTarget(event.target)) {
            touchStart.current = null;
            return;
          }
          const start = touchStart.current;
          const end = event.changedTouches[0]?.clientX;
          touchStart.current = null;
          if (start !== null && end !== undefined && Math.abs(start - end) > 44) {
            navigate(start > end ? activeIndex + 1 : activeIndex - 1);
          }
        }}
      >
        <button
          type="button"
          className="proposal-gallery__arrow proposal-gallery__arrow--previous"
          aria-label="Previous slide"
          disabled={transition !== 'browsing' || settlingClose || activeIndex === 0}
          onClick={() => navigate(activeIndex - 1)}
        >
          ‹
        </button>
        <div
          className="proposal-gallery__carousel"
          style={{ width: viewerStage.width, height: viewerStage.height }}
        >
          {nearby.map(({ slot, index }) => (
            <div
              key={slotId(slot)}
              ref={index === activeIndex ? activeSlideRef : undefined}
              className={`proposal-gallery__slide${index === activeIndex ? ' proposal-gallery__slide--active' : ''}`}
              style={{
                '--gallery-entry-delay': transition === 'opening' && phase === 'to' && index !== activeIndex && !reduced ? '450ms' : '0ms',
                '--gallery-relative-shift': `${index < activeIndex ? -18 : 18}px`,
                '--gallery-relative-tilt-y': `${index < activeIndex ? 0.75 : -0.75}deg`,
                '--gallery-relative-tilt-z': `${index < activeIndex ? 0.1 : -0.1}deg`,
                width: viewerStage.width,
                height: viewerStage.height,
                transform: `translateX(${(index - activeIndex) * (viewerStage.width + slideGap)}px)`,
                transition: reduced
                  ? 'none'
                  : `transform 400ms ${EASE}, opacity 220ms ease var(--gallery-entry-delay, 0ms)`,
              } as CSSProperties}
              aria-hidden={index !== activeIndex}
              {...(index !== activeIndex ? ({ inert: '' } as Record<string, string>) : {})}
              onPointerMove={index === activeIndex ? (event) => {
                // Freeze the card at its current pose while an inline editor
                // owns the pointer. Resetting tilt here makes the whole slide
                // subtly jump at the moment display text becomes an input.
                if (isGalleryEditingTarget(event.target)) return;
                updatePointerTilt(event);
              } : undefined}
              onPointerLeave={index === activeIndex ? releasePointerTilt : undefined}
            >
              <div
                className="proposal-gallery__slide-direction"
                style={{
                  '--gallery-navigation-shift': `${navigationLean * 8}px`,
                  '--gallery-navigation-tilt-y': `${navigationLean}deg`,
                  '--gallery-navigation-tilt-z': `${navigationLean * 0.125}deg`,
                } as CSSProperties}
              >
                <div
                  ref={index === activeIndex ? activePointerRef : undefined}
                  className="proposal-gallery__slide-pointer"
                >
                  {slot.kind === 'pending' ? (
                    <PendingGallerySlide entry={slot.entry} />
                  ) : (
                    <SlideRender
                      proposal={proposal}
                      section={slot.value}
                      prefix={`viewer-${slot.key}-`}
                      actions={actions}
                      readOnly={readOnly}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="proposal-gallery__arrow proposal-gallery__arrow--next"
          aria-label="Next slide"
          disabled={
            transition !== 'browsing' ||
            settlingClose ||
            activeIndex === sequence.length - 1
          }
          onClick={() => navigate(activeIndex + 1)}
        >
          ›
        </button>
      </main>
      {messageBar ? <div className="proposal-gallery__message-bar">{messageBar}</div> : null}
      <div
        className={`proposal-gallery__shared proposal-gallery__shared--${transition} proposal-gallery__shared--${phase}${origin ? '' : ' proposal-gallery__shared--fade'}`}
        aria-hidden="true"
        style={{
          width: viewerStage.width,
          height: viewerStage.height,
          left: viewerStage.left,
          top: viewerStage.top,
          transform: sharedTransform,
        }}
        onTransitionEnd={handleSharedTransitionEnd}
      >
        {active.kind === 'pending' ? (
          <PendingGallerySlide entry={active.entry} />
        ) : (
          <SlideRender proposal={proposal} section={active.value} prefix="shared-" />
        )}
      </div>
    </div>,
    document.body,
  );
}
